import { useEffect } from 'react';
import * as amplitude from '@amplitude/analytics-browser';
import { sha256 } from '../sha256';
import { setAmplitudeUser, trackEvent } from '../analytics';
import useSessionInfo from '../../data/queryHooks/useSessionInfo';
import { AnalyticsEvents } from '../../scripts/miscConstants';

/**
 * Sets and clears the anonymous Amplitude user identity based on session state.
 *
 * - When a member is authenticated and `eligMemberId` is available, sets a
 *   SHA256-hashed user ID in Amplitude so events are stitched across sessions.
 * - If hashing fails, opts the member out of Amplitude tracking entirely rather
 *   than sending an unencrypted identifier.
 */
export function useAmplitudeIdentity() {
	const { eligMemberId, isLoggedIn, ssId } = useSessionInfo();

	useEffect(() => {
		if (!isLoggedIn || !eligMemberId || !ssId) return;

		Promise.all([sha256(eligMemberId), sha256(ssId)])
			.then(([hashedId, hashedSsId]) => {
				setAmplitudeUser(hashedId);
				trackEvent(AnalyticsEvents.MEMBER_LOGGED_IN, undefined, {
					// insert_id deduplicates across reloads and new tabs, rotates on each new login
					insert_id: `${hashedId}-${hashedSsId}`,
				});
			})
			.catch(() => {
				amplitude.setOptOut(true);
			});
	}, [eligMemberId, isLoggedIn, ssId]);
}
